"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import dynamic from "next/dynamic";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { testimonialCards } from "../index";
import { Arrow } from "./Arrow";

const ReviewSlide = dynamic(() => import("./ReviewSlide"));

const useHoverRef = (onHoverChange: (hovered: boolean) => void) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const handleEnter = () => onHoverChange(true);
    const handleLeave = () => onHoverChange(false);

    node.addEventListener("mouseenter", handleEnter);
    node.addEventListener("mouseleave", handleLeave);

    return () => {
      node.removeEventListener("mouseenter", handleEnter);
      node.removeEventListener("mouseleave", handleLeave);
    };
  }, [onHoverChange]);

  return ref;
};

interface Props {
  testimonials: testimonialCards[];
}

const ReviewSlider: React.FC<Props> = ({ testimonials  }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const autoplayTimer = useRef<NodeJS.Timeout | null>(null);

  const getSliderConfig = () => {
    const isMobileView = windowWidth !== null && windowWidth < 768;

    if (isMobileView) {
      return {
        loop: true,
        mode: "snap" as const,
        rtl: false,
        slides: { spacing: 10, origin: "center" as const },
        slideChanged(slider: any) {
          setCurrentSlide(slider.track.details.rel);
        },
        created() {
          setLoaded(true);
        },
      };
    }

    return {
      loop: true,
      mode: "snap" as const,
      rtl: false,
      slides: { perView: "auto" as const, origin: "center" as const },
      slideChanged(slider: any) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
    };
  };

  const [sliderRef, instanceRef] =
    useKeenSlider<HTMLDivElement>(getSliderConfig());

  const clearAutoplay = () => {
    if (autoplayTimer.current) clearTimeout(autoplayTimer.current);
  };

  const startAutoplay = useCallback(() => {
    clearAutoplay();
    if (!isHovered && instanceRef.current) {
      autoplayTimer.current = setTimeout(() => {
        instanceRef.current?.next();
      }, 6000);
    }
  }, [isHovered, instanceRef]);

  useEffect(() => {
    startAutoplay();
    return clearAutoplay;
  }, [currentSlide, isHovered, startAutoplay]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const containerRef = useHoverRef(setIsHovered);
  const arrowsRef = useHoverRef(setIsHovered);

  const getCenteredDots = (
    current: number,
    total: number,
    width: number,
  ): number[] => {
    const isMobileView = width < 768;
    const maxDots = isMobileView ? 3 : 7;
    const actualMaxDots = Math.min(maxDots, total);
    const half = Math.floor(actualMaxDots / 2);
    const result: number[] = [];
    const start = current - half;
    for (let i = 0; i < actualMaxDots; i++) {
      const index = (start + i + total) % total;
      result.push(index);
    }
    return result;
  };

  const dotIndices =
    loaded && instanceRef.current && windowWidth !== null
      ? getCenteredDots(currentSlide, testimonials.length, windowWidth)
      : [];

  return (
    <>
      <div className="navigation-wrapper relative" ref={containerRef}>
        <div ref={sliderRef} className="keen-slider">
          {testimonials.map((_, i) => {
            const total = testimonials.length;
            const prev1 = (currentSlide - 1 + total) % total;
            const prev2 = (currentSlide - 2 + total) % total;
            const prev3 = (currentSlide - 3 + total) % total;
            const next1 = (currentSlide + 1) % total;
            const next2 = (currentSlide + 2) % total;
            const next3 = (currentSlide + 3) % total;

            let className = "keen-slider__slide number-slide" + (i + 1);
            if (i === currentSlide) className += " active";
            else if (i === prev1) className += " prev-1";
            else if (i === prev2) className += " prev-2";
            else if (i === prev3) className += " prev-3";
            else if (i === next1) className += " next-1";
            else if (i === next2) className += " next-2";
            else if (i === next3) className += " next-3";

            return (
              <div key={i} className={className}>
                <div className="bg-black text-center text-white py-32 hidden">
                  {i + 1}
                </div>
              </div>
            );
          })}
        </div>

        {/* Review Dots */}
        {dotIndices.length > 0 && (
          <div className="py-0 flex justify-center items-center md:gap-[17px] gap-[0px] sm:gap-[8px] md:pb-[61px] pb-8 min-h-[300px] overflow-hidden xl:w-[calc(100%_-_100px)] md:w-[100%] w-full lg:mx-auto mx-0">
            {dotIndices.map((idx) => {
              const total = testimonials.length;
              const prev1 = (currentSlide - 1 + total) % total;
              const prev2 = (currentSlide - 2 + total) % total;
              const prev3 = (currentSlide - 3 + total) % total;
              const next1 = (currentSlide + 1) % total;
              const next2 = (currentSlide + 2) % total;
              const next3 = (currentSlide + 3) % total;

              let dotClass = "rounded ";
              if (idx === currentSlide)
                dotClass += "flex-[1] active-slider mx-[15px]";
              else if ([prev1, next1].includes(idx)) dotClass += "second-slide";
              else if ([prev2, next2].includes(idx)) dotClass += "third-slide";
              else if ([prev3, next3].includes(idx)) dotClass += "four-slide";
              else dotClass += "bg-black";

              const review = testimonials[idx];

              return (
                <div
                  key={`${idx}-${currentSlide}`}
                  className={`${dotClass} review-slide-enter review-slide-enter-active`}
                >
                  <ReviewSlide
                    data={review}
                    variant={idx % 2 === 0 ? "blue" : "green"}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Mobile Dots (Click to Slide) */}
        <div className="md:hidden block">
          <div className="flex justify-center items-center gap-2 px-12 py-6 bg-transparent">
            {testimonials.map((_, idx) => (
              <button
                aria-label="dot"
                key={idx}
                onClick={() => instanceRef.current?.moveToIdx(idx)}
                className={`p-0 relative overflow-hidden ${
                  currentSlide === idx
                    ? "flex-1 min-w-[60px] w-16 h-1 transition-none bg-[#B0D9F7]"
                    : "flex-none w-4 h-1 bg-[#B0D9F7]"
                }`}
              >
                <span
                  className={`absolute top-0 left-0 w-full h-1 ${
                    currentSlide === idx
                      ? "[background:linear-gradient(90.07deg,#8ABC00_-6.96%,#0086E6_93.62%)] transition-all duration-[2000ms] delay-[200ms]"
                      : "left-[-100%] transition-none"
                  }`}
                ></span>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Arrows */}
        <div className="md:block hidden">
          {loaded && instanceRef.current && (
            <div
              ref={arrowsRef}
              className="flex justify-center items-center gap-3 mt-0"
            >
              <Arrow
                left
                onClick={(e) => {
                  e.stopPropagation();
                  instanceRef.current?.prev();
                }}
                disabled={false}
                className="stroke-text-button"
              />
              <div className="pagination-indicator text-sm font-medium">
                <span className="text-subtitle-1 font-bold text-secondary-text">
                  {currentSlide + 1} / {testimonials.length}
                </span>
              </div>
              <Arrow
                onClick={(e) => {
                  e.stopPropagation();
                  instanceRef.current?.next();
                }}
                disabled={false}
                className="stroke-text-button"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ReviewSlider;
